import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { isPaymentGatewayTicket, getTicketProviderDefinition } from '../../utils/ticketRequests';
import ticketService from '../../services/ticketService';
import ProviderConfigSummary from './ProviderConfigSummary';
import TicketFieldList from './TicketFieldList';
import TicketRequestEditor from './TicketRequestEditor';
import { getRecordCreationLevel, getRecordCreationRequestKey, recordCreationFieldGroups, getRecordCreationFieldGroup } from '../../utils/recordCreationTickets';

const RecordCreationPanel = ({ ticket, onUpdate, onEditingChange }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editingConfiguration, setEditingConfiguration] = useState(false);
  const handleEditingChange = useCallback(editing => {
    setEditingConfiguration(editing);
    onEditingChange?.(editing);
  }, [onEditingChange]);
  const level = getRecordCreationLevel(ticket.ticket_type);
  const request = ticket[getRecordCreationRequestKey(ticket.ticket_type)];
  const workflow = ticket.workflow || {};
  const canDecide = workflow[`can_decide_${level}`];
  const canSubmit = workflow[`can_submit_${level}`];
  const merchant = level === 'l1' ? ticket.merchant_id : null;
  const paymentGateway = isPaymentGatewayTicket(ticket);
  const merchantLabel = merchant ? [merchant.name, merchant.merchant_code || merchant.client_code].filter(Boolean).join(' / ') || 'the selected merchant' : '';
  const merchantResult = !merchant ? '' : paymentGateway
    ? ` Assignment ticket opened for ${merchantLabel}; assignment continues in that ticket.`
    : ` Added to ${merchantLabel} as Draft; merchant setup continues in a separate ticket.`;
  const merchantApproval = !merchant ? '.' : paymentGateway
    ? ` and opens an assignment ticket for ${merchantLabel}. The gateway is added after assignment approval.`
    : ` and adds it to ${merchantLabel} as Draft. A separate setup ticket will be opened.`;

  const submit = async (action) => {
    if (editingConfiguration) { setError('Save or cancel configuration edits before making a decision.'); return; }
    setError('');
    setNotice('');
    if (action === 'reject' && !note.trim()) { setError('A rejection note is required.'); return; }
    setSaving(action);
    const res = await ticketService.updateRecordCreationRequest(ticket._id, level, action === 'submit' ? 'submit' : 'decision', { decision: action, note: note.trim(), expected_revision: ticket.request_revision || 0 });
    setSaving('');
    if (!res.success) {
      const refreshed = await ticketService.getTicketById(ticket._id);
      if (refreshed.success) onUpdate(refreshed.data);
      setError(res.message || `Unable to update ${level.toUpperCase()} request.`);
      return;
    }
    onUpdate(res.data);
    setNote('');
    setNotice(action === 'submit' || action === 'approve' ? `${level.toUpperCase()} record created successfully.${merchantResult}` : 'Request rejected.');
  };

  return (
    <>
      <section className="card ticket-content-card">
        {level === 'l0' && <div className="ticket-section-heading"><h3>New {request.label}</h3><span>Requested values</span></div>}
        <TicketRequestEditor ticket={ticket} onUpdate={onUpdate} onEditingChange={handleEditingChange}>
        {level === 'l0' ? <TicketFieldList fields={request.display_data || []} /> : recordCreationFieldGroups.map(group => {
          const fields = (request.display_data || []).filter(field => getRecordCreationFieldGroup(field.key) === group.key);
          if (!fields.length) return null;
          return <details className="ticket-details-group" key={group.key} open={group.open}>
            <summary>{group.label}</summary>
            <TicketFieldList fields={fields} />
          </details>;
        })}
        {merchant && request.merchant_provider_config && <details className="ticket-details-group">
          <summary>Merchant configuration<span>{merchantLabel} · Draft</span></summary>
          <ProviderConfigSummary config={request.merchant_provider_config} provider={getTicketProviderDefinition(request.data, paymentGateway, request.display_data)} />
        </details>}
        </TicketRequestEditor>
      </section>
      {!(level === 'l1' && request.status === 'pending' && ticket.stage !== 'finance_approval') && <section className="card ticket-content-card ticket-approval-card" aria-label="Request approval">
        <div className="ticket-section-heading"><h3>{level === 'l1' ? 'Final approval' : 'Request approval'}</h3><span>{workflow.current_status_label}</span></div>
        {error && <div role="alert" className="ticket-inline-notice is-error">{error}</div>}
        {notice && <div role="status" className="ticket-inline-notice is-success">{notice}</div>}
        {editingConfiguration && (canDecide || canSubmit) && <p className="ticket-muted">Save or cancel configuration edits before making a decision.</p>}
        {(canDecide || canSubmit) ? (
          <>
            <p className="ticket-muted">{level === 'l1'
              ? canDecide ? `Approve only when the collected information is correct. Approval creates the L1 Master${merchantApproval}` : 'Approval is saved. Retry creation to complete this request.'
              : canDecide ? 'Approve to create the record and complete this ticket.' : 'Approval is saved. Retry creation to complete this request.'}</p>
            <label>{canDecide ? 'Decision note' : 'Submit note'}
              <textarea className="input" rows="2" value={note} onChange={event => { setNote(event.target.value); setError(''); }} placeholder={canDecide ? 'Add a note. Required if rejecting.' : 'Optional note'} disabled={!!saving || editingConfiguration} />
            </label>
            <div className="ticket-approval-actions">
              {canDecide ? <>
                <button type="button" className="btn btn-secondary" onClick={() => submit('reject')} disabled={!!saving || editingConfiguration}>{saving === 'reject' ? 'Rejecting...' : 'Reject request'}</button>
                <button type="button" className="btn btn-primary" onClick={() => submit('approve')} disabled={!!saving || editingConfiguration}>{saving === 'approve' ? 'Approving...' : level === 'l1' ? 'Approve & create L1' : 'Approve & create record'}</button>
              </> : <button type="button" className="btn btn-primary" onClick={() => submit('submit')} disabled={!!saving || editingConfiguration}>{saving === 'submit' ? 'Creating...' : 'Retry creation'}</button>}
            </div>
          </>
        ) : <p className="ticket-muted">{request.status === 'submitted'
          ? `${level.toUpperCase()} record created.${merchantResult}`
          : request.status === 'rejected' ? 'This request was rejected. The decision and original values are saved in the journal.'
          : level === 'l1' ? 'Continue collecting information and handing this ticket between owners as needed. Finance or Super Admin gives final approval when it is ready.' : 'Waiting for Finance or Superadmin to approve or create this record.'}</p>}
        <div className="ticket-decision-history">
          {request.decided_by_username && <span>Decision by {request.decided_by_username} · {new Date(request.decided_at).toLocaleString()}</span>}
          {request.submitted_by_username && <span>Created by {request.submitted_by_username} · {new Date(request.submitted_at).toLocaleString()}</span>}
        </div>
        {request.status === 'submitted' && request.merchant_ticket_id && <Link className="btn btn-primary" style={{ marginTop: '1rem' }} to={`/tickets/${request.merchant_ticket_id}`}>Open merchant assignment ticket</Link>}
        {request.status === 'submitted' && request.can_view_record && <Link className="btn btn-secondary" style={{ marginTop: '1rem' }} to={request.record_path}>View {request.label} records</Link>}
      </section>}
    </>
  );
};

export default RecordCreationPanel;
